import { useState } from 'react';
import { useSelector } from 'react-redux';

import { Button } from '~/eds/src/components/Button/Button';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Text } from '~/eds/src/components/Text/Text';
import { ManageSyncPairStageType } from '~/enums/ManageSyncPairStageType';
import { OnboardingIdType } from '~/features/onboarding/OnboardingIdType';
import { useIntl } from 'react-intl';
import { slices } from '~/redux/slices';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import PageLayout from '~/ui/components/PageLayout/PageLayout';
import styles from '~/ui/styles';

import {
  testIsAmzS3SyncPair,
  testIsAribaSyncPair,
  testIsValidSyncPair,
} from '../util';
import AribaHeader from './AribaHeader';
import AmazonS3Auth from './Content/AmazonS3Auth';
import AribaMultiAPISetup from './Content/AribaMultiAPISetup';
import Authentication from './Content/Authentication';
import FieldMappingSetup from './Content/FieldMappingSetup';
import FileConfigSetup from './Content/FileConfigSetup';
import FolderAriba from './Content/FolderAriba';
import Folders from './Content/Folders';
import ProcurementProvider from './Content/ProcurementProvider';
import Provider from './Content/Provider';
import Summary from './Content/Summary';
import SummaryAriba from './Content/SummaryAriba';
import Header from './Header';

function ManageSyncPair() {
  const intl = useIntl();
  const activeSyncPair = useSelector(
    slices.integrations.selectors.selectActiveSyncPair,
  );
  const manageSyncPairStage = useSelector(
    slices.integrations.selectors.selectManageSyncPairStage,
  );
  const [enableGuide, setEnableGuide] = useState(true);
  const [showInstruction, setShowInstruction] = useState(false);

  if (manageSyncPairStage === ManageSyncPairStageType.None) {
    return null;
  }

  const isValidSyncPair = testIsValidSyncPair(activeSyncPair);
  const isAmzS3SyncPair = testIsAmzS3SyncPair(activeSyncPair);
  var isAribaSyncPair = testIsAribaSyncPair(activeSyncPair);

  const ARIBA_MULTI_DESCRIPTION_TEXT = intl.formatMessage({
    id: 'ManageSyncPair.aribaMultiDescription',
    defaultMessage:
      'Specify the credentials for External Approval API, Operational Reporting API - Asynchronous, and Strategic Contract Workspace API.',
    description: 'Description for Ariba multi-API setup',
  });

  const getContent = (manageSyncPairStage) => {
    switch (manageSyncPairStage) {
      case ManageSyncPairStageType.Authentication:
        return isAmzS3SyncPair
          ? {
              title: showInstruction
                ? null
                : enableGuide
                  ? intl.formatMessage({
                      id: 'ManageSyncPair.syncingAwsS3',
                      defaultMessage: 'Syncing AWS S3',
                      description: 'Title for AWS S3 sync setup',
                    })
                  : intl.formatMessage({
                      id: 'ManageSyncPair.provideAwsRoleArnDetails',
                      defaultMessage: 'Provide AWS Role ARN details',
                      description: 'Title for AWS Role ARN setup',
                    }),
              description: showInstruction
                ? null
                : intl.formatMessage({
                    id: 'ManageSyncPair.awsS3SyncDescription',
                    defaultMessage:
                      'In order to synchronize documents and folders on your S3 bucket with Evisort, you first need to configure it correctly for use with Evisort.',
                    description: 'Description for AWS S3 synchronization setup',
                  }),
              content: () => (
                <AmazonS3Auth
                  enableGuide={enableGuide}
                  showInstruction={showInstruction}
                  onToggleEnableGuide={setEnableGuide}
                  onToggleShowInstruction={setShowInstruction}
                />
              ),
            }
          : {
              id: OnboardingIdType.LogIntoProvider,
              title: intl.formatMessage({
                id: 'ManageSyncPair.provideAuthenticationDetails',
                defaultMessage: 'Provide authentication details',
                description: 'Title for authentication setup',
              }),
              description: intl.formatMessage({
                id: 'ManageSyncPair.authenticationDescription',
                defaultMessage:
                  'Specify the account details that will be used to access your files and folders, in order to keep them synchronized with Evisort. This account should have full access to any folder you may wish to synchronize with Evisort.',
                description: 'Description for authentication setup',
              }),
              content: Authentication,
            };
      case ManageSyncPairStageType.Folders:
        return {
          title: intl.formatMessage({
            id: 'ManageSyncPair.pickFoldersAndFileTypes',
            defaultMessage: 'Pick the folders and file types you wish to sync',
            description: 'Title for folder and file type selection',
          }),
          content: Folders,
        };
      case ManageSyncPairStageType.Provider:
        return {
          title: `${
            isValidSyncPair
              ? intl.formatMessage({
                  id: 'ManageSyncPair.changeStorageProvider',
                  defaultMessage: 'Change',
                  description: 'Action to change storage provider',
                })
              : intl.formatMessage({
                  id: 'ManageSyncPair.selectStorageProvider',
                  defaultMessage: 'Select',
                  description: 'Action to select storage provider',
                })
          } ${intl.formatMessage({
            id: 'ManageSyncPair.storageProvider',
            defaultMessage: 'storage provider',
            description: 'Storage provider label',
          })}`,
          description: isValidSyncPair
            ? intl.formatMessage({
                id: 'ManageSyncPair.changeProviderWarning',
                defaultMessage:
                  'This will reset authentication, folders, and filetypes.',
                description: 'Warning when changing storage provider',
              })
            : null,
          content: Provider,
        };
      case ManageSyncPairStageType.ProviderProcurements:
        isAribaSyncPair = true;
        return {
          title: intl.formatMessage({
            id: 'ManageSyncPair.selectProcurementProvider',
            defaultMessage: 'Select procurement provider',
            description: 'Title for procurement provider selection',
          }),
          content: ProcurementProvider,
        };
      case ManageSyncPairStageType.MultiAPIs:
        return {
          title: intl.formatMessage({
            id: 'ManageSyncPair.provideAuthenticationDetailsMultiApi',
            defaultMessage: 'Provide authentication details',
            description: 'Title for multi-API authentication setup',
          }),
          description: ARIBA_MULTI_DESCRIPTION_TEXT,
          content: AribaMultiAPISetup,
        };
      case ManageSyncPairStageType.FileConfig:
        return {
          title: intl.formatMessage({
            id: 'ManageSyncPair.importOrSyncFiles',
            defaultMessage: 'Import or sync your files',
            description: 'Title for file import/sync configuration',
          }),
          description: intl.formatMessage({
            id: 'ManageSyncPair.fileConfigDescription',
            defaultMessage:
              'Choose either to do an one-time import of existing or continuously synchronization for data from Ariba.',
            description: 'Description for file configuration options',
          }),
          content: FileConfigSetup,
        };
      case ManageSyncPairStageType.FolderAriba:
        return {
          title: intl.formatMessage({
            id: 'ManageSyncPair.selectFoldersAndDocuments',
            defaultMessage:
              'Select the folders and documents you wish to sync with Evisort',
            description: 'Title for Ariba folder and document selection',
          }),
          content: FolderAriba,
        };
      case ManageSyncPairStageType.SummaryAriba:
        return {
          title: intl.formatMessage({
            id: 'ManageSyncPair.readyToCreateSyncPairAriba',
            defaultMessage: 'Ready to create a new sync pair',
            description: 'Title for Ariba sync pair creation summary',
          }),
          description: intl.formatMessage({
            id: 'ManageSyncPair.aribaSyncStartDescription',
            defaultMessage:
              'Once you confirm, the sync will begin. This might take some time depending on the number of documents that need to be synced.',
            description: 'Description for Ariba sync start process',
          }),
          content: SummaryAriba,
        };
      case ManageSyncPairStageType.Summary:
        return {
          title: intl.formatMessage({
            id: 'ManageSyncPair.readyToCreateSyncPair',
            defaultMessage: 'Ready to create a new sync pair',
            description: 'Title for sync pair creation summary',
          }),
          description: intl.formatMessage({
            id: 'ManageSyncPair.syncStartDescription',
            defaultMessage:
              'Once you confirm, the synchronization process will begin. This might take some time, depending on the number of documents that need to be synced.',
            description: 'Description for sync start process',
          }),
          content: Summary,
        };
      case ManageSyncPairStageType.FieldMapping:
        return {
          title: intl.formatMessage({
            id: 'ManageSyncPair.mapFieldsBetweenEvisortAndAriba',
            defaultMessage:
              'Map the fields when syncing between Evisort and Ariba',
            description: 'Title for field mapping setup',
          }),
          content: FieldMappingSetup,
        };
      default:
        return null;
    }
  };

  const {
    description,
    title,
    content: Content,
    id,
  } = getContent(manageSyncPairStage);

  const footer = showInstruction && (
    <PageLayout.Footer
      leftButton={
        <Button
          variant="secondary"
          text={intl.formatMessage({
            id: 'ManageSyncPair.backButton',
            defaultMessage: 'Back',
            description: 'Back button text',
          })}
          onClick={() => {
            setShowInstruction(false);
            setEnableGuide(true);
          }}
        />
      }
      actionButtons={[
        <Button
          text={intl.formatMessage({
            id: 'ManageSyncPair.iHaveMyIamRoleArn',
            defaultMessage: 'I have my IAM Role ARN',
            description: 'Button text for confirming IAM Role ARN availability',
          })}
          variant="primary"
          onClick={() => {
            setShowInstruction(false);
            setEnableGuide(false);
          }}
        />,
      ]}
    />
  );

  return (
    <PageLayout
      header={
        isAribaSyncPair ? (
          <AribaHeader />
        ) : (
          <Header onToggleEnableGuide={setEnableGuide} />
        )
      }
      footer={footer}
    >
      <FlexLayout
        alignItems="center"
        flexDirection="column"
        py={8}
        space={6}
        sx={styles.layout.container}
      >
        <Text color="text.primary" fontSize="20px" fontWeight="700">
          {title}
        </Text>
        {description && (
          <Text textAlign="center" whiteSpace="pre-wrap">
            <Markdown text={description} />
          </Text>
        )}
        <FlexLayout id={id} justifyContent="center" sx={{ width: '100%' }}>
          <Content />
        </FlexLayout>
      </FlexLayout>
    </PageLayout>
  );
}

export default ManageSyncPair;
